import "./Renewals.scss";
import { useState, useEffect } from "react";
import { Modal, Table } from "antd";
import { withLocale } from "../../l10n";
import service from "../../services/Service";
import { GetColumnLiteralSearchProps } from "../../utils/functions";
import { formatDate, formatDateTime, compareDates } from '../../utils/dates';


const getActionMap = (lang) => ({
  'created':             lang.handle('Creación'),
  'deleted':             lang.handle('Eliminación'),
  'activated':           lang.handle('Activación'),
  'deactivated':         lang.handle('Desactivación'),
  'canceled':            lang.handle('Cancelación'),
  'prepared':            lang.handle('Preparación'),
  'renewed':             lang.handle('Renovación'),
  'change-interval':     lang.handle('Cambio de intervalo'),
  'change-subscription': lang.handle('Cambio de plan'),
  'updated':             lang.handle('Actualización'),
});

const HistoryModal = ({ open, selectedRow, subscriptionType, onClose, lang }) => {

  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setHistoryData([]);
      return;
    }

    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const res = await service.get(`subscriptions-history/${subscriptionType}/${selectedRow?.uuid}`);
        if (res?.status === 200 && res.data?.ok) {
          setHistoryData(res.data.data);
        } else {
          setHistoryData([]);
        }
      } catch (err) {
        console.log("Error fetching history: " + err);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchHistory();
  }, [open, selectedRow, subscriptionType]);

  // Recupera el registro mas actual que no sea delete para obtener la información de la cabecera del histórico.
  const getFormatHeaderInfo = () => {

    if (!selectedRow) return null;

    return {
      uuid: selectedRow.uuid? selectedRow.uuid : '—',
      renewal_date: selectedRow.renewal_date ? formatDate(selectedRow.renewal_date) : '—',
      connection_date: selectedRow.firts_connection_date ? formatDate(selectedRow.firts_connection_date) : '—',
      renewal_interval: selectedRow.renewal_interval ?? null,
    };
  };

  // Obtiene y da formato a los datos a mostrar en la columna cambios de nuestra tabla.
  const renderChangesCell = (data) => {
    if (!data.old && !data.new) return <span className="history-no-data">—</span>;

    const DATE_FIELDS = ['hiring_date', 'renewal_date', 'ki_connection_date'];

    const formatForDisplay = (field, val) => {

      if (val === null || val === undefined) return '—';

      if (DATE_FIELDS.includes(field)) {
        return formatDate(val) || String(val);
      }
      
      return String(val);
    };

    const { old: oldData, new: newData } = data;
    const source = oldData || newData;
    const keys = Object.keys(source).filter(k => k !== 'device_uuid');
    const diffs = keys.filter(k =>
      formatForDisplay(k, oldData?.[k]) !== formatForDisplay(k, newData?.[k])
    );

    const fieldLabelMap = {
      renewal_date:          lang.handle('Fecha de renovación'),
      renewal_interval:      lang.handle('Intervalo de renovación'),
      ki_subscription_state: lang.handle('Estado'),
      state:                 lang.handle('Estado'),
      ki_subscription_uuid:  lang.handle('Plan'),
      ki_connection_date:    lang.handle('Fecha de primera conexión'),
      hiring_date:           lang.handle('Fecha primera contratación'),
    };

    if (diffs.length === 0) return <span className="history-no-data">—</span>;

    return (
      <div className="history-cell history-changes">
        {diffs.map(changingField => (
          <div key={changingField} className="history-diff-row">
            <span className="history-diff-key">{fieldLabelMap[changingField] ?? changingField}</span>
            <span className="history-diff-old">{formatForDisplay(changingField, oldData?.[changingField])}</span>
            <span className="history-diff-arrow">→</span>
            <span className="history-diff-new">{formatForDisplay(changingField, newData?.[changingField])}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      title={lang.handle('Histórico de suscripción')}
      open={open}
      onCancel={onClose}
      footer={null}
      width={950}
    >
    {/* ENCABEZADO FIJO */}
      {(() => {
        const headerInfo = getFormatHeaderInfo();
        return (
          <>
            {headerInfo && (
              <div className="history-header-info">
                <div className="history-header-item">
                  <span className="history-header-label">{lang.handle('Identificador')}</span>
                  <span className="history-header-value history-header-uuid">{headerInfo.uuid}</span>
                </div>
                <div className="history-header-item">
                  <span className="history-header-label">{lang.handle('Fecha de primera conexión')}</span>
                  <span className="history-header-value">{headerInfo.connection_date}</span>
                </div>
                <div className="history-header-item">
                  <span className="history-header-label">{lang.handle('Fecha de renovación')}</span>
                  <span className="history-header-value">{headerInfo.renewal_date}</span>
                </div>
                <div className="history-header-item">
                  <span className="history-header-label">{lang.handle('Intervalo de renovación')}</span>
                  <span className="history-header-value">
                    {headerInfo.renewal_interval !== null
                      ? `${headerInfo.renewal_interval} ${lang.handle('meses')}`
                      : '—'}
                  </span>
                </div>
              </div>
            )}
          </>
        );
      })()}
      <Table
        size="small"
        loading={historyLoading}
        dataSource={historyData}
        rowKey="id"
        pagination={false}
        scroll={{ y: 300 }}
        columns={[
          {
            title: lang.handle('Fecha'),
            dataIndex: 'changed_ts',
            width: '14%',
            defaultSortOrder: 'descend',
            sorter: (a, b) => compareDates(a.changed_ts, b.changed_ts),
            render: (ts) => {
              const [day, time] = formatDateTime(ts).split(' ');
              return (
                <span className="history-date-cell">
                  <div>{day}</div>
                  <div className="history-date-time">{time}</div>
                </span>
              );
            },
          },
          {
            title: lang.handle('Acción'),
            dataIndex: 'action',
            width: '18%',
            filters: Object.entries(getActionMap(lang)).map(([value, text]) => ({ text, value })),
            onFilter: (value, record) => record.action === value,
            render: (action) => {
              const actionMap = getActionMap(lang);
              return <span className="history-cell">{actionMap[action] ?? action}</span>;
            }
          },
          {
            title: lang.handle('Pedido'),
            dataIndex: 'order_id',
            width: '14%',
            ...GetColumnLiteralSearchProps('order_id', lang),
            render: (order_id) => (
              <span className="history-cell">{order_id || '—'}</span>
            ),
          },
          {
            title: lang.handle('Realizado por'),
            dataIndex: 'changed_by_username',
            width: '15%',
            render: (username) => (
              <span className="history-cell">{username ?? '-'}</span>
            ),
          },
          {
            title: lang.handle('Cambios'),
            dataIndex: 'data',
            width: '39%',
            render: renderChangesCell,
          },
        ]}
      />
    </Modal>
  );
};

export default withLocale(HistoryModal);
